import { useEffect, useState } from 'react';
import { Alert, Platform, ScrollView, Text, View } from 'react-native';
import { ROLAGEM_TECLADO, useTecladoAndroid } from '../components/teclado';
import { useAuth } from './AuthContext';
import { Btn, Field } from '../components/ui';
import { Padrao } from '../components/Padrao';
import { C } from '../components/theme';
import { mascaraCpf } from '../lib/cpf';
import { t } from '../lib/i18n';

const COR_APP = '#8B5CF6';

/** Tela de desbloqueio do app. Com onOk, serve também para destravar uma aba (texto 10, D: components/TravaAba):
 *  mesmo desbloqueio (senha, PIN, desenho ou biometria), com título próprio e "Voltar" no lugar de "Entrar com outra conta". */
export default function LockScreen({ titulo, cor, onOk, onVoltar }: { titulo?: string; cor?: string; onOk?: () => void; onVoltar?: () => void } = {}) {
  const a = useAuth();
  const teclado = useTecladoAndroid();   // Android: a tela não encolhe com o teclado (o fim da página ganha o espaço dele)
  const COR = cor ?? COR_APP;
  const [senha, setSenha] = useState('');
  const [busy, setBusy] = useState(false);
  const [modo, setModo] = useState<'padrao' | 'senha' | 'pin'>(a.padrao ? 'padrao' : a.pin ? 'pin' : 'senha');
  const [pin, setPin] = useState('');
  const [erros, setErros] = useState(0);
  const [errou, setErrou] = useState(false);

  async function desenhou(seq: string) {
    if (await a.unlockPadrao(seq)) { onOk?.(); return; }
    setErrou(true); setTimeout(() => setErrou(false), 700);
    const n = erros + 1; setErros(n);
    if (n >= 5) { setModo('senha'); Alert.alert(t('Muitas tentativas'), t('Use a senha para desbloquear.')); }
  }

  const pelaBio = () => a.unlockBio().then(ok => { if (ok) onOk?.(); }).catch(() => {});
  useEffect(() => { if (a.bio) pelaBio(); if (!onOk) a.avisarRosto(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  async function entrarPin(p = pin) {
    if (p.length < 4) return;
    setBusy(true);
    try { if (await a.unlockPin(p)) onOk?.(); else { setErrou(true); setTimeout(() => setErrou(false), 900); } }
    catch (e) { Alert.alert(t('Aguarde um pouco'), e instanceof Error ? e.message : String(e)); }
    finally { setBusy(false); setPin(''); }
  }

  async function entrar() {
    setBusy(true);
    // no navegador não há bloqueio do app (unlockSenha aceita tudo): a trava da aba confere a senha da conta
    const conferir = onOk && Platform.OS === 'web' ? a.conferirSenha : a.unlockSenha;
    try { if (await conferir(senha)) onOk?.(); else Alert.alert(t('Senha incorreta')); }
    catch (e) { Alert.alert(t('Aguarde um pouco'), e instanceof Error ? e.message : String(e)); }
    finally { setBusy(false); setSenha(''); }
  }

  return (
    // texto 18 B: rola (antes era uma tela fixa: com o teclado aberto o campo podia ficar escondido e sem rolar) e o
    // teclado sai do caminho; tocar fora do campo fecha o teclado
    <ScrollView style={{ flex: 1, backgroundColor: C.bg }} {...ROLAGEM_TECLADO} keyboardShouldPersistTaps="handled"
      contentContainerStyle={{ flexGrow: 1, justifyContent: 'center', padding: 28, paddingBottom: 28 + teclado }}>
      <View style={{ gap: 14 }}>
        <Text style={{ fontSize: 44, textAlign: 'center' }}>🔒</Text>
        <Text style={{ color: C.text, fontSize: 22, fontWeight: '800', textAlign: 'center' }}>{titulo ?? t('My Planner bloqueado')}</Text>
        <Text style={{ color: C.sub, textAlign: 'center' }}>{a.nome ? `${a.nome} · ` : ''}{a.cpf ? `CPF ${mascaraCpf(a.cpf)}` : a.email ?? ''}</Text>
        {modo === 'pin' ? (<>
          <Text style={{ color: errou ? C.danger : C.sub, textAlign: 'center' }}>{errou ? t('PIN incorreto') : t('Digite o seu PIN')}</Text>
          <Field value={pin} onChangeText={v => { const d = v.replace(/\D/g, '').slice(0, 6); setPin(d); }} placeholder={t('PIN')} secureTextEntry keyboardType="number-pad" maxLength={6} onSubmitEditing={() => entrarPin()} autoFocus />
          <Btn cor={COR} label={busy ? t('Aguarde...') : t('Desbloquear')} onPress={() => entrarPin()} disabled={busy || pin.length < 4} />
          <Btn cor={COR} outline label={t('Usar senha')} onPress={() => setModo('senha')} />
        </>) : modo === 'padrao' ? (<>
          <Text style={{ color: errou ? C.danger : C.sub, textAlign: 'center' }}>{errou ? t('Desenho incorreto') : t('Desenhe o seu padrão')}</Text>
          <Padrao cor={COR} erro={errou} onFim={desenhou} />
          <Btn cor={COR} outline label={t('Usar senha')} onPress={() => setModo('senha')} />
        </>) : (<>
          <Field value={senha} onChangeText={setSenha} placeholder={t('Senha')} secureTextEntry autoCapitalize="none" onSubmitEditing={entrar} />
          <Btn cor={COR} label={busy ? t('Aguarde...') : t('Desbloquear')} onPress={entrar} disabled={busy || !senha} />
          {a.padrao && erros < 5 && <Btn cor={COR} outline label={t('Usar desenho padrão')} onPress={() => setModo('padrao')} />}
          {a.pin && <Btn cor={COR} outline label={t('Usar o PIN')} onPress={() => setModo('pin')} />}
        </>)}
        {a.bioDisponivel && <Btn cor={COR} outline label={'📱 ' + t('Biometria ou código do dispositivo')} onPress={pelaBio} />}
        {onVoltar ? <Text onPress={onVoltar} style={{ color: C.sub, textAlign: 'center', marginTop: 10 }}>‹ {t('Voltar')}</Text>
        : Platform.OS === 'web' ? <Text onPress={() => a.signOut()} style={{ color: C.sub, textAlign: 'center', marginTop: 10 }}>{t('Entrar com o QR Code')}</Text>
        : <Text onPress={() => Alert.alert(t('Sair da conta?'), t('Os dados continuam salvos na nuvem.'), [
          { text: t('Cancelar'), style: 'cancel' }, { text: t('Sair'), style: 'destructive', onPress: () => a.signOut() }])}
          style={{ color: C.sub, textAlign: 'center', marginTop: 10 }}>{t('Entrar com outra conta')}</Text>}
      </View>
    </ScrollView>
  );
}
